import { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faUnlink } from '@fortawesome/free-solid-svg-icons';

import { formatMoney } from '../../utils/format-money';
import { formatDate, formatXpensPeriod } from './statement-display';

const SKELETON_CARDS = 4;

const FILTERS = ['all', 'matched', 'unmatched'];

// Account activity that can never carry a match - a payment, a fee reversal -
// is not work left to do, so it stays out of the unmatched tally the way the
// backend counters keep it out. It is still listed under "all".
const isUnmatched = (item) => !item.match && item.status !== 'ignored';

const matchesFilter = (item, filter) => {
    if (filter === 'matched') return Boolean(item.match);
    if (filter === 'unmatched') return isUnmatched(item);

    return true;
};

const countFilters = (items) => ({
    all: items.length,
    matched: items.filter((item) => item.match).length,
    unmatched: items.filter(isUnmatched).length,
});

const listSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    listStyle: 'none',
    m: 0,
    p: 0,
};

const cardSx = {
    borderColor: '#dee2e6',
    borderRadius: '0.5rem',
    boxShadow: 'none',
    overflow: 'hidden',
};

// The statement line reads as the card header: what the issuer printed.
const lineSx = {
    // Top-aligned so a description that wraps at narrow widths keeps the amount
    // on the same line as the badge rather than centred against the wrap.
    alignItems: 'flex-start',
    display: 'flex',
    gap: 1.5,
    justifyContent: 'space-between',
    px: 2,
    py: 1.5,
};

const lineBadgeSx = {
    backgroundColor: '#f1f3f5',
    borderRadius: '0.25rem',
    color: '#5e6c84',
    flexShrink: 0,
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    px: 0.75,
    py: 0.25,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
};

const dateSx = {
    color: 'text.secondary',
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
};

const amountSx = {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    whiteSpace: 'nowrap',
};

// The XPENS side sits beneath its line, on a tinted band so the pair reads as
// one block rather than two rows that happen to sit next to each other.
const matchSx = {
    backgroundColor: '#fafbfc',
    borderTop: '1px solid #f1f3f5',
    px: 2,
    py: 1.5,
};

const matchBodySx = {
    alignItems: 'center',
    display: 'flex',
    gap: 1.5,
    justifyContent: 'space-between',
    pl: 1.5,
};

const markSx = (matched) => ({
    alignItems: 'center',
    backgroundColor: matched ? '#d9f4e4' : '#f1f3f5',
    borderRadius: '50%',
    color: matched ? '#167443' : '#a5adba',
    display: 'flex',
    flexShrink: 0,
    fontSize: '0.75rem',
    height: 24,
    justifyContent: 'center',
    width: 24,
});

const chipSx = {
    backgroundColor: '#d9f4e4',
    borderRadius: '999px',
    color: '#167443',
    fontSize: '0.6875rem',
    fontWeight: 600,
    px: 0.75,
    py: 0.125,
    whiteSpace: 'nowrap',
};

const createdChipSx = {
    backgroundColor: '#e6f0ff',
    borderRadius: '999px',
    color: '#0747a6',
    fontSize: '0.6875rem',
    fontWeight: 600,
    px: 0.75,
    py: 0.125,
    whiteSpace: 'nowrap',
};

const subTextSx = {
    color: 'text.secondary',
    display: 'block',
    mt: 0.25,
};

const filterGroupSx = {
    backgroundColor: '#f1f3f5',
    border: '1px solid #dee2e6',
    borderRadius: '0.5rem',
    display: 'inline-flex',
    gap: 0.25,
    p: 0.25,
};

const filterButtonSx = (active) => ({
    backgroundColor: active ? '#fff' : 'transparent',
    border: 0,
    borderRadius: '0.375rem',
    boxShadow: active ? '0 1px 2px rgba(9, 30, 66, 0.15)' : 'none',
    color: active ? '#172b4d' : '#5e6c84',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: active ? 600 : 500,
    px: 1.25,
    py: 0.5,
    whiteSpace: 'nowrap',

    '&:hover:not(:disabled)': {
        color: '#172b4d',
    },

    '&:disabled': {
        color: '#a5adba',
        cursor: 'default',
    },

    '&:focus-visible': {
        outline: '2px solid #0052cc',
        outlineOffset: '-2px',
    },
});

const translate = (data, key, fallback = key) => {
    const value = data?.t?.read?.(key);

    return typeof value === 'string' || typeof value === 'number'
        ? value
        : fallback;
};

// Statement lines are numbered by the order they were printed, so the label
// stays stable across reloads and lines up with the source document.
const lineNumber = (item, index) => {
    const source = Number.isInteger(item?.source_index)
        ? item.source_index + 1
        : index + 1;

    return String(source).padStart(3, '0');
};

const StatementLinesTable = ({
                                 data,
                                 items = [],
                                 loading = false,
                                 loadFailed = false,
                                 busyItemId = null,
                                 onLink,
                                 onUnlink,
                             }) => {
    // Which lines are on screen is this list's own concern, so it is local
    // state rather than store state.
    const [filter, setFilter] = useState('all');

    const emptyMessage = () => {
        if (loadFailed) {
            return translate(
                data,
                'cc_statements.statement_reconciliation.load_failed',
                'Statement lines could not be loaded.'
            );
        }

        return translate(
            data,
            'cc_statements.statement_reconciliation.table.no_lines',
            'No statement lines were extracted.'
        );
    };

    const renderStatementLine = (item, index) => (
        <Box sx={lineSx} data-testid='statement-line-source'>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    minWidth: 0,
                }}
            >
                <Box component='span' sx={lineBadgeSx}>
                    {`${translate(
                        data,
                        'cc_statements.statement_reconciliation.table.line',
                        'Line'
                    )} ${lineNumber(item, index)}`}
                </Box>

                <Typography variant='body2' sx={dateSx}>
                    {formatDate(item.transaction_date || item.posted_date) || '—'}
                </Typography>

                <Typography
                    variant='body2'
                    sx={{
                        fontWeight: 600,
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                    }}
                >
                    {item.description || '—'}
                </Typography>
            </Box>

            <Typography variant='body2' sx={amountSx}>
                {formatMoney(item.amount, item.currency || null) || '—'}
            </Typography>
        </Box>
    );

    const renderPeriod = (period) => {
        if (!period) {
            return (
                <Typography component='span' variant='caption' sx={subTextSx}>
                    {translate(
                        data,
                        'cc_statements.statement_reconciliation.period_unassigned',
                        'Not assigned to a period'
                    )}
                </Typography>
            );
        }

        return (
            <Typography component='span' variant='caption' sx={subTextSx}>
                {`${translate(
                    data,
                    'cc_statements.statement_reconciliation.table.xpens_period',
                    'Statement period'
                )}: `}
                <Box
                    component='strong'
                    sx={{ color: 'text.primary', fontWeight: 600 }}
                >
                    {period}
                </Box>
            </Typography>
        );
    };

    const renderMatched = (item, period, busy) => {
        const transaction = item.match?.transaction || {};

        return (
            <Box sx={matchSx} data-testid='statement-line-match'>
                <Box sx={matchBodySx}>
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 1.5,
                            minWidth: 0,
                        }}
                    >
                        <Box sx={markSx(true)}>
                            <FontAwesomeIcon icon={faLink} aria-hidden='true' />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant='body2'
                                    sx={{
                                        fontWeight: 500,
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {transaction.description || '—'}
                                </Typography>

                                <Typography
                                    component='span'
                                    variant='caption'
                                    sx={dateSx}
                                >
                                    {formatDate(
                                        transaction.authorized_date ||
                                        transaction.posted_date
                                    ) || '—'}
                                </Typography>

                                <Box component='span' sx={chipSx}>
                                    {translate(
                                        data,
                                        'cc_statements.statement_reconciliation.table.matched',
                                        'Matched'
                                    )}
                                </Box>

                                {item?.resolution?.kind === 'created_line_item' && (
                                    <Box component='span' sx={createdChipSx}>
                                        {translate(
                                            data,
                                            'cc_statements.statement_reconciliation.table.created_here',
                                            'Created here'
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {renderPeriod(period)}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexShrink: 0,
                            gap: 1.5,
                            ml: 'auto',
                        }}
                    >
                        <Typography
                            variant='body2'
                            sx={{ ...amountSx, color: 'text.secondary' }}
                        >
                            {formatMoney(
                                transaction.amount,
                                transaction.currency || null
                            ) || '—'}
                        </Typography>

                        <button
                            type='button'
                            className='btn btn-sm btn-outline-secondary'
                            disabled={busy}
                            onClick={() => onUnlink?.(item)}
                        >
                            {busy && <span
                                className='spinner-border spinner-border-sm mr-2'
                                role='status'
                                aria-hidden='true'
                            />}
                            {translate(
                                data,
                                'cc_statements.statement_reconciliation.unlink_transaction',
                                'Unlink'
                            )}
                        </button>
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderUnmatched = (item, busy) => {
        const ignored = item.status === 'ignored';

        return (
            <Box sx={matchSx} data-testid='statement-line-match'>
                <Box sx={matchBodySx}>
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 1.5,
                            minWidth: 0,
                        }}
                    >
                        <Box sx={markSx(false)}>
                            <FontAwesomeIcon icon={faUnlink} aria-hidden='true' />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant='body2'
                                sx={{
                                    color: 'text.secondary',
                                    fontStyle: 'italic',
                                }}
                            >
                                {ignored
                                    ? translate(
                                        data,
                                        'cc_statements.statement_reconciliation.table.ignored',
                                        'Account activity (not matched)'
                                    )
                                    : translate(
                                        data,
                                        'cc_statements.statement_reconciliation.table.unmatched',
                                        'Unmatched'
                                    )}
                            </Typography>

                            {renderPeriod(null)}
                        </Box>
                    </Box>

                    {!ignored && (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flexShrink: 0,
                                gap: 1,
                                ml: 'auto',
                            }}
                        >
                            <button
                                type='button'
                                className='btn btn-sm btn-outline-secondary'
                                disabled={busy}
                                onClick={() => onLink?.(item)}
                            >
                                {busy && <span
                                    className='spinner-border spinner-border-sm mr-2'
                                    role='status'
                                    aria-hidden='true'
                                />}
                                {translate(
                                    data,
                                    'cc_statements.statement_reconciliation.link_transaction',
                                    'Link transaction'
                                )}
                            </button>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const renderSkeletonCard = (row) => (
        <Paper
            component='li'
            key={`skeleton-${row}`}
            variant='outlined'
            sx={cardSx}
            data-testid='statement-lines-skeleton'
        >
            <Box sx={lineSx}>
                <Skeleton variant='text' width='45%' />
                <Skeleton variant='text' width='15%' />
            </Box>

            <Box sx={matchSx}>
                <Box sx={matchBodySx}>
                    <Skeleton variant='text' width='40%' />
                    <Skeleton variant='text' width='20%' />
                </Box>
            </Box>
        </Paper>
    );

    const counts = countFilters(items);

    // Unmatching the last matched line would leave the matched filter showing
    // nothing, so a filter that has run out of lines falls back to all.
    const activeFilter = counts[filter] > 0 ? filter : 'all';

    const visibleRows = items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => matchesFilter(item, activeFilter));

    const renderFilters = () => (
        <Box
            role='group'
            aria-label={translate(
                data,
                'cc_statements.statement_reconciliation.filter.label',
                'Filter statement lines'
            )}
            sx={filterGroupSx}
        >
            {FILTERS.map((name) => (
                <Box
                    component='button'
                    key={name}
                    type='button'
                    aria-pressed={activeFilter === name}
                    disabled={counts[name] === 0}
                    onClick={() => setFilter(name)}
                    sx={filterButtonSx(activeFilter === name)}
                >
                    {`${translate(
                        data,
                        `cc_statements.statement_reconciliation.filter.${name}`,
                        name
                    )} (${counts[name]})`}
                </Box>
            ))}
        </Box>
    );

    return (
        <Box
            component='section'
            className='mt-5'
            aria-labelledby='statement-lines-title'
        >
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <Typography
                    id='statement-lines-title'
                    component='h4'
                    variant='h6'
                    sx={{ fontWeight: 600, mb: 0 }}
                >
                    {translate(
                        data,
                        'cc_statements.statement_reconciliation.statement_lines',
                        'Statement lines'
                    )}
                </Typography>

                {!loading && items.length > 0 && renderFilters()}
            </Box>

            {!loading && items.length === 0 && (
                <Paper
                    variant='outlined'
                    sx={{ ...cardSx, py: 5, textAlign: 'center' }}
                >
                    <Typography
                        variant='body2'
                        role={loadFailed ? 'alert' : undefined}
                        sx={{ color: 'text.secondary' }}
                    >
                        {emptyMessage()}
                    </Typography>
                </Paper>
            )}

            <Box component='ul' sx={listSx}>
                {loading &&
                    Array.from({ length: SKELETON_CARDS }).map((_, row) =>
                        renderSkeletonCard(row)
                    )}

                {!loading && visibleRows.map(({ item, index }) => {
                    const busy = busyItemId === item.id;
                    const match = item.match;

                    const period = match
                        ? formatXpensPeriod(match.xpens_statement, data.t)
                        : null;

                    return (
                        <Paper
                            component='li'
                            key={item.id}
                            variant='outlined'
                            sx={cardSx}
                            data-testid='statement-line-card'
                        >
                            {renderStatementLine(item, index)}

                            {match
                                ? renderMatched(item, period, busy)
                                : renderUnmatched(item, busy)}
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
};

export default StatementLinesTable;
